import * as XLSX from 'xlsx';
import Papa from 'papaparse';

export interface ParsedRow {
  email: string;
  targetMinutes: number;
  rowNumber: number;
}

export interface ValidationResult {
  email: string;
  targetMinutes: number;
  currentMinutes: number;
  delta: number;
  status: 'Valid' | 'Invalid Email' | 'Not Found' | 'Duplicate' | 'Invalid Minutes' | 'Missing Data';
  errorMessage?: string;
  rowNumber: number;
}

export interface ParseResult {
  success: boolean;
  rows: ParsedRow[];
  errors: string[];
}

/**
 * Normalizes header names to standard format
 */
const normalizeHeader = (header: string): string => {
  return header.trim().toLowerCase().replace(/\s+/g, ' ');
};

/**
 * Validates that required headers are present
 */
const validateHeaders = (headers: string[]): { valid: boolean; errors: string[] } => {
  const normalized = headers.map(normalizeHeader);
  const errors: string[] = [];

  const requiredHeaders = {
    person: ['person', 'email', 'email address', 'user email'],
    totalMinutes: ['total minutes', 'minutes', 'til minutes', 'balance minutes'],
  };

  const hasPersonHeader = requiredHeaders.person.some(h => normalized.includes(h));
  const hasTotalMinutesHeader = requiredHeaders.totalMinutes.some(h => normalized.includes(h));

  if (!hasPersonHeader) {
    errors.push('Missing required header: "Person" (email address). Accepted variations: Person, Email, Email Address, User Email');
  }

  if (!hasTotalMinutesHeader) {
    errors.push('Missing required header: "Total Minutes". Accepted variations: Total Minutes, Minutes, TIL Minutes, Balance Minutes');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Finds the column index for a given header type
 */
const findColumnIndex = (headers: string[], headerType: 'person' | 'totalMinutes'): number => {
  const normalized = headers.map(normalizeHeader);

  const patterns = {
    person: ['person', 'email', 'email address', 'user email'],
    totalMinutes: ['total minutes', 'minutes', 'til minutes', 'balance minutes'],
  };

  return normalized.findIndex(h => patterns[headerType].includes(h));
};

/**
 * Validates email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates that the value is a non-negative integer
 */
const isValidMinutes = (value: any): boolean => {
  const num = Number(value);
  return !isNaN(num) && Number.isInteger(num) && num >= 0;
};

/**
 * Parses CSV file using Papa Parse
 */
const parseCSV = (file: File): Promise<ParseResult> => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      complete: (results) => {
        try {
          const data = results.data as string[][];

          if (data.length < 2) {
            resolve({
              success: false,
              rows: [],
              errors: ['File is empty or contains only headers'],
            });
            return;
          }

          // Extract headers and validate
          const headers = data[0].map(h => String(h || '').trim());
          const headerValidation = validateHeaders(headers);

          if (!headerValidation.valid) {
            resolve({
              success: false,
              rows: [],
              errors: headerValidation.errors,
            });
            return;
          }

          // Find column indices
          const emailColIndex = findColumnIndex(headers, 'person');
          const minutesColIndex = findColumnIndex(headers, 'totalMinutes');

          // Parse rows
          const rows: ParsedRow[] = [];
          const errors: string[] = [];

          for (let i = 1; i < data.length; i++) {
            const row = data[i];

            // Skip empty rows
            if (!row || row.every(cell => !cell || String(cell).trim() === '')) {
              continue;
            }

            const email = String(row[emailColIndex] || '').trim();
            const minutesStr = String(row[minutesColIndex] || '').trim();

            if (!email && !minutesStr) {
              continue; // Skip blank lines
            }

            if (!email || !minutesStr) {
              errors.push(`Row ${i + 1}: Missing email or minutes`);
              continue;
            }

            const targetMinutes = Number(minutesStr);

            rows.push({
              email,
              targetMinutes,
              rowNumber: i + 1,
            });
          }

          resolve({
            success: true,
            rows,
            errors,
          });
        } catch (error) {
          resolve({
            success: false,
            rows: [],
            errors: [`Error parsing CSV: ${error instanceof Error ? error.message : String(error)}`],
          });
        }
      },
      error: (error) => {
        resolve({
          success: false,
          rows: [],
          errors: [`CSV parsing error: ${error.message}`],
        });
      },
    });
  });
};

/**
 * Parses Excel file using SheetJS
 */
const parseExcel = (file: File): Promise<ParseResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        // Read first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to array of arrays
        const sheetData: string[][] = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          raw: false,
          defval: '',
        }) as string[][];

        if (sheetData.length < 2) {
          resolve({
            success: false,
            rows: [],
            errors: ['File is empty or contains only headers'],
          });
          return;
        }

        // Extract headers and validate
        const headers = sheetData[0].map(h => String(h || '').trim());
        const headerValidation = validateHeaders(headers);

        if (!headerValidation.valid) {
          resolve({
            success: false,
            rows: [],
            errors: headerValidation.errors,
          });
          return;
        }

        // Find column indices
        const emailColIndex = findColumnIndex(headers, 'person');
        const minutesColIndex = findColumnIndex(headers, 'totalMinutes');

        // Parse rows
        const rows: ParsedRow[] = [];
        const errors: string[] = [];

        for (let i = 1; i < sheetData.length; i++) {
          const row = sheetData[i];

          // Skip empty rows
          if (!row || row.every(cell => !cell || String(cell).trim() === '')) {
            continue;
          }

          const email = String(row[emailColIndex] || '').trim();
          const minutesStr = String(row[minutesColIndex] || '').trim();

          if (!email && !minutesStr) {
            continue; // Skip blank lines
          }

          if (!email || !minutesStr) {
            errors.push(`Row ${i + 1}: Missing email or minutes`);
            continue;
          }

          const targetMinutes = Number(minutesStr);

          rows.push({
            email,
            targetMinutes,
            rowNumber: i + 1,
          });
        }

        resolve({
          success: true,
          rows,
          errors,
        });
      } catch (error) {
        resolve({
          success: false,
          rows: [],
          errors: [`Error parsing Excel: ${error instanceof Error ? error.message : String(error)}`],
        });
      }
    };

    reader.onerror = () => {
      resolve({
        success: false,
        rows: [],
        errors: ['Failed to read file'],
      });
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Main parse function that handles both CSV and Excel
 */
export const parseImportFile = async (file: File): Promise<ParseResult> => {
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (extension === 'csv') {
    return parseCSV(file);
  } else if (extension === 'xlsx' || extension === 'xls') {
    return parseExcel(file);
  } else {
    return {
      success: false,
      rows: [],
      errors: ['Unsupported file format. Please upload a .csv or .xlsx file.'],
    };
  }
};

/**
 * Validates parsed rows against user database
 */
export const validateRows = (
  rows: ParsedRow[],
  users: Array<{ email: string; tilBalance: number }>
): ValidationResult[] => {
  const userMap = new Map(users.map(u => [u.email.toLowerCase(), u.tilBalance]));
  const emailCount = new Map<string, number>();
  const results: ValidationResult[] = [];

  // Count email occurrences
  rows.forEach(row => {
    const emailLower = row.email.toLowerCase();
    emailCount.set(emailLower, (emailCount.get(emailLower) || 0) + 1);
  });

  // Track which emails we've seen (for duplicate detection)
  const seenEmails = new Map<string, number>();

  rows.forEach(row => {
    const emailLower = row.email.toLowerCase();
    const isDuplicate = (emailCount.get(emailLower) || 0) > 1;
    const seenCount = seenEmails.get(emailLower) || 0;
    seenEmails.set(emailLower, seenCount + 1);

    let status: ValidationResult['status'] = 'Valid';
    let errorMessage: string | undefined;
    let currentMinutes = 0;

    // Validate email format
    if (!isValidEmail(row.email)) {
      status = 'Invalid Email';
      errorMessage = 'Email format is invalid';
    }
    // Validate minutes
    else if (!isValidMinutes(row.targetMinutes)) {
      status = 'Invalid Minutes';
      errorMessage = 'Total Minutes must be a non-negative integer';
    }
    // Check if user exists
    else if (!userMap.has(emailLower)) {
      status = 'Not Found';
      errorMessage = 'User not found in system';
    }
    // Check for duplicates (keep only last occurrence)
    else if (isDuplicate && seenCount < (emailCount.get(emailLower) || 0)) {
      status = 'Duplicate';
      errorMessage = `Duplicate entry (row ${row.rowNumber}). Only the last occurrence will be used.`;
      currentMinutes = userMap.get(emailLower) || 0;
    }
    // Valid row
    else {
      currentMinutes = userMap.get(emailLower) || 0;
      if (isDuplicate) {
        status = 'Valid';
        errorMessage = 'Using this row (last occurrence)';
      }
    }

    const delta = row.targetMinutes - currentMinutes;

    results.push({
      email: row.email,
      targetMinutes: row.targetMinutes,
      currentMinutes,
      delta,
      status,
      errorMessage,
      rowNumber: row.rowNumber,
    });
  });

  return results;
};

/**
 * Gets summary statistics from validation results
 */
export const getSummaryStats = (results: ValidationResult[]) => {
  const stats = {
    total: results.length,
    valid: 0,
    skipped: 0,
    notFound: 0,
    duplicates: 0,
    toUpdate: 0,
    noChange: 0,
  };

  const lastOccurrences = new Map<string, ValidationResult>();

  results.forEach(result => {
    const emailLower = result.email.toLowerCase();

    if (result.status === 'Valid') {
      lastOccurrences.set(emailLower, result);
    } else if (result.status === 'Not Found') {
      stats.notFound++;
      stats.skipped++;
    } else if (result.status === 'Duplicate') {
      stats.duplicates++;
    } else {
      stats.skipped++;
    }
  });

  // Count valid updates
  lastOccurrences.forEach(result => {
    stats.valid++;
    if (result.delta === 0) {
      stats.noChange++;
    } else {
      stats.toUpdate++;
    }
  });

  return stats;
};
